import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { PageHeader } from "@/components/page-header";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SetupPanel } from "@/components/setup-panel";
import { MetricToggleChart } from "@/components/metric-chart";
import { ChartCard, EmptyChart } from "@/components/charts/chart-card";
import { RankList } from "@/components/charts/rank-list";
import { AttributionCard } from "@/components/charts/attribution-card";
import { FunnelCard } from "@/components/charts/funnel-card";
import { ParamChips } from "@/components/analytics/param-chips";
import { CompareNote, DataNote } from "@/components/analytics/report-shell";
import { StoreSampleBanner } from "@/components/analytics/store-sample-banner";
import { SkStorePage } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { DEFAULT_PRESET } from "@shared/analytics-range";
import { defaultGrain, grainOptions, withRange } from "@shared/store-report";
import type { OverviewPage } from "@shared/pages/overview";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<OverviewPage>("overview", deps));
  },
  component: OverviewRoute,
});

function OverviewRoute() {
  useTitle("Overview");
  const search = Route.useSearch();
  return (
    <PageQuery<OverviewPage> name="overview" search={search} skeleton={<SkStorePage />}>
      {(page) => <Overview page={page} />}
    </PageQuery>
  );
}

function Overview({ page }: { page: OverviewPage }) {
  const { range: r, status } = page;
  return (
    <div className="page-shell">
      <PageHeader title="Overview" actions={<DateRangeFilter presets={r.presets} defaultKey={DEFAULT_PRESET} compare />} />
      <StoreSampleBanner sales={page.sources.sales === "sample"} ads={page.sources.ads === "sample"} ga={page.sources.ga === "sample"} />
      <DataNote status={status} staleAfterDays={3} />
      {status && !status.onFile.orders && (
        <SetupPanel title="No Shopify orders on file yet" className="mb-4">
          Shopify is connected. Orders show here after the first sync finishes.
        </SetupPanel>
      )}

      <MetricToggleChart
        labels={page.labels}
        metrics={page.metrics}
        height="clamp(200px, 17cqw, 280px)"
        hint={`Shopify sales and orders from Shopify, ad spend from Google Ads, visits from Google Analytics.${page.hasPrevious ? " Dashed lines are the previous period." : ""}`}
        right={<ParamChips param="by" options={grainOptions()} defaultValue={defaultGrain(r)} />}
      />
      <CompareNote range={r} />

      <div className="mt-4 grid gap-4 @3xl:grid-cols-2 @6xl:grid-cols-3">
        <ChartCard
          title="Top products"
          right={
            <Link href={withRange("/products", r.params)} className="text-xs font-semibold text-accent-dark hover:underline">
              All products
            </Link>
          }
          hint="Shopify sales by product, from Shopify."
        >
          <RankList items={page.products.items} emptyText="No products sold in this range." />
        </ChartCard>
        <AttributionCard title="Where orders came from" views={page.attribution} />
        <FunnelCard
          className="@3xl:col-span-2 @6xl:col-span-1"
          title="From visit to order"
          hint="From Google Analytics."
          unit="shoppers"
          steps={page.funnel.steps}
          empty={<EmptyChart text={page.funnel.steps ? undefined : page.funnel.empty} />}
        />
      </div>
    </div>
  );
}
